import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Category } from '../../utils/categories';
import { categoryDisplayName, findCategory, galleryInfoCopy, normalizeCopy, splitParagraphs } from '../../utils/galleryInfoCopy';
import './GalleryInfoDialog.css';

interface GalleryInfoDialogProps {
  name: string;
  category?: string;
  excerpt?: string;
  description?: string;
  categories?: Category[];
  onClose: () => void;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const GalleryInfoDialog: React.FC<GalleryInfoDialogProps> = ({
  name,
  category,
  excerpt,
  description,
  categories,
  onClose,
}) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const [resolvedCategories, setResolvedCategories] = useState<Category[]>(categories || []);
  const copy = galleryInfoCopy(excerpt, description);
  const categoryLabel = categoryDisplayName(category, resolvedCategories);
  const matchedCategory = findCategory(category, resolvedCategories);
  const categoryParagraphs = splitParagraphs(matchedCategory?.description || '').filter((paragraph) => {
    const normalized = normalizeCopy(paragraph);
    if (!normalized || normalized === normalizeCopy(copy.intro)) return false;
    return !copy.paragraphs.some((existing) => normalizeCopy(existing) === normalized);
  });
  const markSrc = `${process.env.PUBLIC_URL || ''}/brand/carmen-lorraine-mark.svg`;

  onCloseRef.current = onClose;

  useEffect(() => {
    if (categories && categories.length > 0) {
      setResolvedCategories(categories);
      return;
    }
    let cancelled = false;
    import('../../utils/CategoryAPI')
      .then(({ loadLiveCategories }) => loadLiveCategories())
      .then((loaded) => {
        if (!cancelled && Array.isArray(loaded) && loaded.length > 0) {
          setResolvedCategories(loaded);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [categories]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const background = document.querySelector('.gallery-lightbox');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('gallery-info-open');
    if (background) background.setAttribute('inert', '');
    dialog.focus();

    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
      return !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && dialog.contains(active);
      if (event.shiftKey) {
        if (!inside || active === first || active === dialog) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last || active === dialog) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('gallery-info-open');
      if (background) background.removeAttribute('inert');
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);

  const dialog = (
    <div className="gallery-info-dialog-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        id="gallery-info-dialog"
        className="gallery-info-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="gallery-info-dialog-close"
          onClick={onClose}
          aria-label="Close gallery information"
        >
          <span aria-hidden="true">×</span>
        </button>
        <div className="gallery-info-dialog-scroll">
          <header className="gallery-info-dialog-header">
            <img
              className="gallery-info-dialog-mark"
              src={markSrc}
              alt=""
              width={46}
              height={46}
            />
            <span className="gallery-info-dialog-rule" aria-hidden="true" />
            {categoryLabel && (
              <p className="gallery-info-dialog-category">{categoryLabel}</p>
            )}
            <h2 id={titleId} className="gallery-info-dialog-title">{name}</h2>
          </header>
          {copy.intro && (
            <p className="gallery-info-dialog-excerpt">{copy.intro}</p>
          )}
          {copy.paragraphs.length > 0 && (
            <div className="gallery-info-dialog-body">
              {copy.paragraphs.map((paragraph, index) => (
                <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
              ))}
            </div>
          )}
          {categoryParagraphs.length > 0 && (
            <div className="gallery-info-dialog-body">
              {categoryParagraphs.map((paragraph, index) => (
                <p key={`category-${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default GalleryInfoDialog;
